/**
 * Shared helpers for location dropdown (react-select) used in Add Booking, Send Review Request, etc.
 */
import React from 'react'

export const getLocationName = (location) => {
  if (location.location_identifier) {
    return location.location_identifier
  }
  if (location.locationName) {
    return location.locationName
  }
  if (location.displayname) {
    const parts = location.displayname.split(',')
    if (parts.length >= 1) {
      return parts[0].trim()
    }
  }
  return location.name || ''
}

// Returns city only (source of truth: Add Booking two-line format - name + city, never street address)
export const getLocationAddress = (location) => {
  if (location.city) {
    return location.city.trim()
  }
  if (location.displayname) {
    const parts = location.displayname.split(',').map((p) => p.trim())
    // Only extract city when format is "Name, Address, City, State" (3+ parts)
    if (parts.length >= 3) {
      return parts[parts.length - 2]
    }
    // With 2 parts ("Name, Address"), don't show address - would be wrong format
  }
  return ''
}

export const formatLocationOptionLabel = (option) => {
  const location = option.location || option
  const locationName = getLocationName(location)
  const address = getLocationAddress(location)

  return (
    <div className="d-flex flex-column">
      <div>{locationName}</div>
      {address ? (
        <div className="text-muted" style={{ fontSize: '0.875rem' }}>
          {address}
        </div>
      ) : null}
    </div>
  )
}

// Check if searchTerm appears as a subsequence in text (chars in order, e.g. "inge" matches "Inglewood")
const isSubsequenceMatch = (text, searchTerm) => {
  const textLower = String(text).toLowerCase()
  const termLower = String(searchTerm).toLowerCase()
  let termIndex = 0
  for (let i = 0; i < textLower.length && termIndex < termLower.length; i++) {
    if (textLower[i] === termLower[termIndex]) {
      termIndex++
    }
  }
  return termIndex === termLower.length
}

// Build searchable text from location - matches LocationFilterUniversal (b-location-filter) fields
const getSearchableFields = (location) => {
  const fields = [
    location.name,
    location.location_identifier,
    location.locationName,
    location.displayname,
    location.address,
    location.address2,
    location.city,
    location.state,
    location.zip,
    location.postalCode
  ]

  // Parse displayname into parts (e.g. "Business, 123 Main St, Inglewood, CA" -> each part searchable)
  if (location.displayname) {
    const parts = location.displayname
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
    fields.push(...parts)
  }

  return fields.filter(Boolean)
}

export const filterLocationOptions = (option, inputValue) => {
  if (inputValue == null || inputValue === '' || (typeof inputValue === 'string' && inputValue.trim() === '')) {
    return true
  }

  const searchTerm = String(inputValue).toLowerCase().trim()
  if (!searchTerm) {
    return true
  }

  // react-select passes { label, value, data: rawOption } to filterOption; rawOption has { value, label, location }
  const rawOption = option.data || option
  const location = rawOption.location || rawOption

  if (!location || typeof location !== 'object') {
    if (option.label) {
      const label = option.label.toLowerCase()
      return label.includes(searchTerm) || isSubsequenceMatch(label, searchTerm)
    }
    return true
  }

  // Use same fields as LocationFilterUniversal (b-location-filter w-fit-w)
  const searchableFields = [
    ...getSearchableFields(location),
    getLocationName(location),
    getLocationAddress(location),
    option.label,
    String(option.value || '')
  ].filter(Boolean)

  const searchableText = searchableFields.map((field) => String(field).toLowerCase()).join(' ')

  // Exact substring match (e.g. "ingle" matches "Inglewood")
  const exactMatch = searchableText.includes(searchTerm)
  const individualFieldExactMatch = searchableFields.some((field) => {
    const fieldStr = String(field).toLowerCase()
    return fieldStr.includes(searchTerm)
  })

  // Subsequence match for partial typing (e.g. "inge" matches "Inglewood")
  const subsequenceMatch = searchableFields.some((field) => isSubsequenceMatch(String(field), searchTerm))

  return exactMatch || individualFieldExactMatch || subsequenceMatch
}

// Grey hover/focus for location options - overrides react-select's default blue (applied as inline styles)
const LOCATION_OPTION_HOVER_BG = '#edf1f5'

export const locationSelectStyles = {
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused || state.isSelected ? LOCATION_OPTION_HOVER_BG : provided.backgroundColor,
    color: state.isFocused || state.isSelected ? '#082344' : provided.color,
    cursor: 'pointer'
  })
}
